import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, } from 'recharts';
import { FiRefreshCw, FiAlertTriangle, FiTrendingUp, FiChevronDown, FiArrowRight, } from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import { fetchYoyData, fetchTrailing12mData, fetchForecast30dData, fetchForecast90dData, fetchArAgingData, } from '../../services/commissionForecastService';
import type { DisplayMonthRow, DisplaySnapshot, DisplayBucket, DisplayScenario, DisplayArRow, DisplayLobEntry, } from '../../services/commissionForecastService';
import '../../styles/CommissionForecast.css';
const fmtK = (n: number) => {
    if (Math.abs(n) >= 1000000)
        return `$${(n / 1000000).toFixed(1)}M`;
    if (Math.abs(n) >= 1000)
        return `$${(n / 1000).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
};
const fmtPct = (n: number, d = 1) => `${n.toFixed(d)}%`;
const fmtNum = (n: number) => n.toLocaleString();
const Sk = ({ w = '100%', h = 13, r = 6 }: {
    w?: string | number;
    h?: number;
    r?: number;
}) => (<span className="cf-skel" style={{ display: 'block', width: w, height: h, borderRadius: r }}/>);
const Empty = ({ msg = 'No data available', sub = 'This section will populate once commission data is received from the API.', }: {
    msg?: string;
    sub?: string;
}) => (<div className="cf-empty">
    <FiAlertTriangle size={24} className="cf-empty__icon"/>
    <div className="cf-empty__msg">{msg}</div>
    <div className="cf-empty__sub">{sub}</div>
  </div>);
const DarkTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length)
        return null;
    return (<div className="cf-tooltip">
      <p className="cf-tooltip__label">{label}</p>
      {payload.map((p: any, i: number) => (<p key={i} className="cf-tooltip__row" style={{ color: p.color }}>
          <span>{p.name}:</span> <strong>{fmtK(p.value)}</strong>
        </p>))}
    </div>);
};
const ChangeCell = ({ cur, prev, raw = false }: {
    cur: number;
    prev: number;
    raw?: boolean;
}) => {
    const diff = cur - prev;
    const pct = prev !== 0 ? (diff / Math.abs(prev)) * 100 : 0;
    if (diff > 0)
        return (<td className="cf-tbl__change cf-tbl__change--up">
      <FiTrendingUp size={11}/> {raw ? `+${fmtNum(diff)}` : `+${fmtK(diff)}`} <span>({fmtPct(pct)})</span>
    </td>);
    if (diff < 0)
        return (<td className="cf-tbl__change cf-tbl__change--down">
      <FiChevronDown size={11}/> {raw ? fmtNum(diff) : fmtK(diff)} <span>({fmtPct(pct)})</span>
    </td>);
    return <td className="cf-tbl__change cf-tbl__change--flat"><FiArrowRight size={11}/> —</td>;
};
interface InfoTipConfig {
    title: string;
    body: string;
    formula?: string;
    note?: string;
}
const InfoTip: React.FC<{
    cfg: InfoTipConfig;
}> = ({ cfg }) => {
    const [vis, setVis] = React.useState(false);
    return (<span className="cf-tip-wrap" onMouseEnter={() => setVis(true)} onMouseLeave={() => setVis(false)}>
      <button className="cf-tip-btn" type="button" aria-label="More info" onClick={e => e.stopPropagation()}>?</button>
      {vis && (<span className="cf-tip-popup" role="tooltip">
          <span className="cf-tip-popup__arrow"/>
          <span className="cf-tip-popup__title">{cfg.title}</span>
          <span className="cf-tip-popup__body">{cfg.body}</span>
          {cfg.formula && <span className="cf-tip-popup__formula">{cfg.formula}</span>}
          {cfg.note && <span className="cf-tip-popup__note">{cfg.note}</span>}
        </span>)}
    </span>);
};
const KPI_TIPS: Record<string, InfoTipConfig> = {
    earned: {
        title: 'Commission Earned MTD',
        body: 'The total agency commission calculated on all policies with an effective date in the current calendar month — regardless of whether the cash has been received yet. This is an accrual figure, not a cash figure. It includes new business, renewals, and endorsements written this month.',
        formula: '= SUM(AgencyCommission) WHERE EffectiveDate = this month',
        note: 'Benchmark: top quartile agencies grow commission 8–12% YoY',
    },
    received: {
        title: 'Commission Received MTD',
        body: 'Cash actually deposited — the subset of Commission Earned that has been paid out by carriers or collected from insureds (agency bill). The gap between the Earned and Received is your outstanding A/R. For direct bill carriers, this lags 7–45 days behind the effective date depending on the carrier.',
        formula: '= SUM(AgencyCommissionPaid) WHERE EffectiveDate = this month',
        note: 'Healthy collection rate: 95%+ by Day 30 of next month',
    },
    forecast30: {
        title: '30-Day Commission Forecast',
        body: 'Expected commission cash arriving in the next 30 days from four sources: (A) renewals expiring × 87% retention, (B) new business already written × 95% certainty, (C) minus pending cancellation return commissions × 70%, (D) plus prior-period A/R still outstanding × 70% collection rate. Based only on already-booked business — no estimates.',
        formula: '= Bucket A + B − C + D (certainty-adjusted)',
        note: 'This is an operational tool — confidence level 85–95%',
    },
    forecast90: {
        title: '90-Day Commission Forecast',
        body: 'Projected commission for the next 3 months using: renewal pipeline expiring × 87% retention probability + trailing 3-month new business average × trend factor + average fee income. The base case uses industry-average retention (87%). Conservative uses 83%, Optimistic uses 89%. This is a planning tool — confidence level 60–80%.',
        formula: '= (Expiring commission × retention%) + (3M NB avg × trend) + fees',
        note: 'Revenue risk range shown: Optimistic minus Conservative',
    },
    ar: {
        title: 'Accounts Receivable Outstanding',
        body: 'Total commission earned but not yet received across ALL prior periods — not just this month. Includes direct bill commissions due but not yet remitted by carriers, and agency bill premiums invoiced but not yet collected from insureds. A growing A/R balance month-over-month signals a collection problem or a slow-pay carrier issue.',
        formula: '= SUM(ReceivablesAmount − ReceivablesPaid) all periods',
        note: 'Watch: 31–60d bucket. Problem: 90+ bucket growing',
    },
    retention: {
        title: 'Policy Retention Rate',
        body: 'Policies renewed this month ÷ policies that expired this month × 100. The single most important metric for commission stability. Each 1% drop in retention is ~1% drop in renewal commission next year compounded.',
        formula: '= COUNT(renewed) / COUNT(expired last month) × 100',
        note: 'Industry: 84–87% avg · Top quartile: 90%+ · Below 80% = crisis',
    },
    directBill: {
        title: 'Billing Type Split',
        body: 'Direct Bill = carrier collects premium, remits commission to agency 30–60 days later. Agency Bill = agency collects premium upfront, retains commission immediately. The split determines your cash timing. More agency bill = faster cash. More direct bill = longer lag but less credit risk.',
        formula: 'BillingType = 0 → Direct Bill · BillingType = 1 → Agency Bill',
        note: 'Typical mix: 70% direct bill / 30% agency bill for personal lines',
    },
    atRisk: {
        title: 'At-Risk Policy Count',
        body: 'Policies expiring in 30 days with NO renewal quote yet + non-payment cancellations still saveable. These two numbers represent immediate revenue that will be lost without action today.',
        formula: 'Unquoted renewals (NOT EXISTS quote) + NonPayment cancels',
        note: 'Target: unquoted renewals = 0 by Day 20 before expiry',
    },
};
const CHART_TIPS: Record<string, InfoTipConfig> = {
    trend12m: {
        title: '12-Month Commission Trend',
        body: 'Trailing 12 months of Commission Earned (purple solid) vs Commission Received (green dashed). The gap between the two lines is your YoY growth. A widening gap = accelerating growth. A narrowing gap = growth slowing. Flat or crossing = revenue declining.',
        formula: 'SUM(AgencyCommission) GROUP BY month, last 24 months',
        note: 'Look for consistent upward slope — top agencies grow 8–12% annually',
    },
    newVsRenew: {
        title: 'New Business vs Renewals',
        body: 'Commission stacked by business type each month. Purple = new clients. Green = existing clients renewing. The new business bar is your growth engine — if it\'s shrinking relative to renewals, the agency is contracting.',
        formula: 'SUM(AgencyCommission) WHERE BusinessType = \'New_Business\' or \'Renewal\'',
        note: 'Healthy: 18–22% new biz · Below 12% = surviving on retention only',
    },
    lob: {
        title: 'Commission by Line of Business',
        body: 'PL (Personal Lines: Auto, Home, Personal) / CL (Commercial: everything else) / Life & Health. Tracks your book composition. An agency shifting from CL to PL typically sees lower average commission per policy.',
        formula: 'SUM(AgencyCommission) grouped by LinesOfBusiness',
        note: 'CL typically earns 12–15% commission · PL 10–12% · Life varies',
    },
    collectedVsOut: {
        title: 'Commission Collected vs Outstanding',
        body: 'Green bar = cash received. Amber bar = commission still owed (Earned − Received). A growing amber bar month-over-month is the first warning sign of a collection problem — either slow carriers or agency bill clients not paying.',
        formula: 'Received = SUM(AgencyCommissionPaid) · Outstanding = Earned − Received',
        note: 'Prior months should be 95%+ green. Current month will always be partial.',
    },
    forecast30sec: {
        title: '30-Day Commission Forecast',
        body: 'Four-bucket model projecting cash commission expected in the next 30 days: Bucket A (renewals), B (new business written), C (pending cancellations — negative), D (prior-period A/R recovery). Based only on already-booked business — no estimates.',
        formula: '= Bucket A + B − C + D (certainty-adjusted)',
        note: 'Confidence interval: 85–95% depending on carrier payment patterns',
    },
    forecast90sec: {
        title: '90-Day Three-Scenario Forecast',
        body: 'Three scenarios built from the same inputs — only the retention rate and new business multiplier change. Conservative: 83% retention, NB ×0.85. Base: 87% retention, NB at trend. Optimistic: 89% retention, NB ×1.20. The revenue risk range (Optimistic minus Conservative) shows planning uncertainty.',
        formula: '= (Expiring commission × retention%) + (3M NB avg × trend × multiplier) + fees',
        note: 'Confidence: 60–80% · Use Base Case for budgeting · Conservative for payroll planning',
    },
    arAging: {
        title: 'A/R Aging Buckets',
        body: 'Breaks outstanding receivables into standard accounting aging periods. The total A/R number alone is misleading — $18K outstanding is fine if it\'s all under 30 days (normal carrier lag). $18K in the 90+ bucket is a serious collection problem. This view tells the real story.',
        formula: '= ReceivablesAmount − ReceivablesPaid, grouped by days since effective date',
        note: '90+ days growing = escalate to carrier or insured immediately',
    },
    trailing12sec: {
        title: 'Trailing 12-Month Detail',
        body: 'Month-by-month table of all key commission metrics for the past 12 months. Use this to spot seasonal patterns, track collection efficiency, and understand your new business vs renewal mix over time.',
        formula: 'All metrics: earned, received, written premium, fee income, % collected',
        note: 'Current month row is highlighted — data updates in real time as policies are written',
    },
};
const COL_TIPS: Record<string, InfoTipConfig> = {
    writtenPremium: {
        title: 'Written Premium',
        body: 'Total written premium for all policies bound in this calendar month, regardless of billing type or commission timing.',
        formula: '= SUM(WrittenPremium) WHERE EffectiveDate in month',
    },
    commEarned: {
        title: 'Commission Earned',
        body: 'Accrual commission calculated on all policies with an effective date in this month. Not cash — this is what was earned regardless of payment status.',
        formula: '= SUM(AgencyCommission) WHERE EffectiveDate in month',
    },
    commReceived: {
        title: 'Commission Received',
        body: 'Cash actually received in this month — direct bill remittances from carriers plus agency bill collections from insureds.',
        formula: '= SUM(AgencyCommissionPaid) WHERE PaymentDate in month',
    },
    pctCollected: {
        title: '% Collected',
        body: 'Commission Received ÷ Commission Earned × 100. Measures collection efficiency. Current month will always be below 100% due to carrier payment lag.',
        formula: '= CommissionReceived / CommissionEarned × 100',
        note: 'Prior months 95%+ = healthy · Below 85% = collection risk',
    },
    newBizPct: {
        title: 'New Biz %',
        body: 'New business commission as a percentage of total commission earned. Tracks the health of your agency growth engine.',
        formula: '= NewBiz_Commission / CommissionEarned × 100',
        note: 'Target: 18–25% of monthly commission from new business',
    },
    feeIncome: {
        title: 'Fee Income',
        body: 'Policy fees, service fees, and inspection fees charged separately from carrier commission. Direct cash revenue not subject to carrier pay cycles.',
        formula: '= SUM(PolicyFees + ServiceFees)',
    },
    totalRevenue: {
        title: 'Total Revenue',
        body: 'Commission Received plus Fee Income — total cash revenue recognized in the month.',
        formula: '= CommissionReceived + FeeIncome',
    },
};
const BUCKET_TIPS: Record<string, InfoTipConfig> = {
    a: {
        title: 'Bucket A — Renewals',
        body: 'Commission from policies expiring in the next 30 days, adjusted for 87% expected retention rate. The largest and most predictable component of your 30-day forecast.',
        formula: '= RenewalCommission × 87% retention',
        note: 'Accounts for ~65–75% of the 30-day forecast',
    },
    b: {
        title: 'Bucket B — New Business',
        body: 'Commission from new business already written month-to-date, at 95% certainty to account for last-minute cancellations before bind.',
        formula: '= NewBizCommission_MTD × 95% certainty',
    },
    c: {
        title: 'Bucket C — Cancellations',
        body: 'Expected return commission from pending cancellations — a negative adjustment. Commission that will be charged back if these policies cancel before the effective date.',
        formula: '= −(PendingCancelCommission × 70% probability)',
        note: 'Non-payment cancels are still saveable with outbound calls today',
    },
    d: {
        title: 'Bucket D — A/R Recovery',
        body: 'Prior-period commission still outstanding in A/R, adjusted at 70% expected collection rate over the next 30 days.',
        formula: '= OutstandingAR × 70% collection rate',
    },
};
const YOY_TIPS: Record<string, InfoTipConfig> = {
    earned: { title: 'Commission Earned', body: 'Total agency commission accrued on policies with an effective date in this month — regardless of payment status.', formula: '= SUM(AgencyCommission) this month vs same month last year' },
    received: { title: 'Commission Received', body: 'Cash commission actually deposited this month from carriers and agency bill collections.', formula: '= SUM(AgencyCommissionPaid) this month vs same month last year' },
    wp: { title: 'Written Premium', body: 'Total premium volume of policies bound this month. Growth in premium = growth in future commission.', formula: '= SUM(WrittenPremium) this month vs same month last year' },
    newBiz: { title: 'New Biz Commission', body: 'Commission from policies written for brand-new clients only. The primary driver of agency growth.', formula: '= SUM(NewBiz_Commission) this month vs same month last year', note: 'Target: 18–25% of total monthly commission' },
    renewal: { title: 'Renewal Commission', body: 'Commission from existing clients renewing their policies. Stable, recurring base revenue.', formula: '= SUM(Renewal_Commission) this month vs same month last year' },
    fee: { title: 'Fee Income', body: 'Policy fees, service charges, and inspection fees collected separately from carrier commission.', formula: '= SUM(PolicyFees + ServiceFees) this month vs same month last year' },
    policies: { title: 'Policies Written', body: 'Count of policies bound this month — new business and renewals combined. Volume metric.', formula: '= COUNT(policies) WHERE EffectiveDate = this month' },
    clients: { title: 'Unique Clients', body: 'Count of distinct insured clients with at least one active policy this month.', formula: '= COUNT(DISTINCT ClientID) WHERE active = true' },
    avgComm: { title: 'Avg Commission / Policy', body: 'Average commission earned per policy bound. Rising avg = writing larger accounts or higher-commission lines.', formula: '= CommissionEarned / PoliciesWritten', note: 'Healthy range: $200–$600 depending on book mix' },
};
const CommissionForecastPage: React.FC = () => {
    const [loadingYoy, setLoadingYoy] = useState(true);
    const [loadingTrailing, setLoadingTrailing] = useState(true);
    const [loading30d, setLoading30d] = useState(true);
    const [loading90d, setLoading90d] = useState(true);
    const [loadingAr, setLoadingAr] = useState(true);
    const [errorYoy, setErrorYoy] = useState(false);
    const [errorTrailing, setErrorTrailing] = useState(false);
    const [error30d, setError30d] = useState(false);
    const [error90d, setError90d] = useState(false);
    const [errorAr, setErrorAr] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [lastRefreshed, setLastRefreshed] = useState('');
    const [trailing12mData, setTrailing12mData] = useState<DisplayMonthRow[]>([]);
    const [currData, setCurrData] = useState<DisplaySnapshot | null>(null);
    const [smlyData, setSmlyData] = useState<DisplaySnapshot | null>(null);
    const [forecast30dData, setForecast30dData] = useState<DisplayBucket[]>([]);
    const [forecast90dData, setForecast90dData] = useState<DisplayScenario[]>([]);
    const [arAgingData, setArAgingData] = useState<DisplayArRow[]>([]);
    const [lobDataState, setLobDataState] = useState<DisplayLobEntry[]>([]);
    const [activeTab, setActiveTab] = useState(0);
    const [earnedVis, setEarnedVis] = useState(true);
    const [receivedVis, setReceivedVis] = useState(true);
    const [newBizVis, setNewBizVis] = useState(true);
    const [renewalVis, setRenewalVis] = useState(true);
    const [collRecVis, setCollRecVis] = useState(true);
    const [collOwedVis, setCollOwedVis] = useState(true);
    const [optVis, setOptVis] = useState(true);
    const [baseVis, setBaseVis] = useState(true);
    const [pessVis, setPessVis] = useState(true);
    const loadYoy = () => {
        setLoadingYoy(true);
        setErrorYoy(false);
        fetchYoyData()
            .then(d => { setCurrData(d.curr); setSmlyData(d.smly); setLobDataState(d.lobData); })
            .catch(() => setErrorYoy(true))
            .finally(() => setLoadingYoy(false));
    };
    const loadTrailing = () => {
        setLoadingTrailing(true);
        setErrorTrailing(false);
        fetchTrailing12mData()
            .then(setTrailing12mData)
            .catch(() => setErrorTrailing(true))
            .finally(() => { setLoadingTrailing(false); setLastRefreshed(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })); });
    };
    const load30d = () => {
        setLoading30d(true);
        setError30d(false);
        fetchForecast30dData()
            .then(setForecast30dData)
            .catch(() => setError30d(true))
            .finally(() => setLoading30d(false));
    };
    const load90d = () => {
        setLoading90d(true);
        setError90d(false);
        fetchForecast90dData()
            .then(setForecast90dData)
            .catch(() => setError90d(true))
            .finally(() => setLoading90d(false));
    };
    const loadAr = () => {
        setLoadingAr(true);
        setErrorAr(false);
        fetchArAgingData()
            .then(setArAgingData)
            .catch(() => setErrorAr(true))
            .finally(() => setLoadingAr(false));
    };
    useEffect(() => {
        loadYoy();
        loadTrailing();
        load30d();
        load90d();
        loadAr();
    }, []);
    const handleRefresh = () => {
        setRefreshing(true);
        Promise.allSettled([
            loadYoy(), loadTrailing(), load30d(), load90d(), loadAr(),
        ]).finally(() => setRefreshing(false));
    };
    const anyLoading = loadingYoy || loadingTrailing || loading30d || loading90d || loadingAr;
    const anyError = errorYoy || errorTrailing || error30d || error90d || errorAr;
    const status = anyLoading ? 'loading' : anyError ? 'error' : 'live';
    const earned = currData?.CommissionEarned ?? 0;
    const received = currData?.CommissionReceived ?? 0;
    const outstanding = earned - received;
    const collPct = earned > 0 ? (received / earned) * 100 : 0;
    const yoyEarnedPct = smlyData?.CommissionEarned
        ? ((earned - smlyData.CommissionEarned) / smlyData.CommissionEarned) * 100 : 0;
    const total30d = forecast30dData.reduce((s, b) => s + b.adjusted, 0);
    const total90dBase = forecast90dData.reduce((s, m) => s + m.baseTotal, 0);
    const total90dPess = forecast90dData.reduce((s, m) => s + m.pessTotal, 0);
    const total90dOpt = forecast90dData.reduce((s, m) => s + m.optTotal, 0);
    const riskPct = total90dBase > 0
        ? Math.abs((total90dBase - total90dPess) / total90dBase * 100) : 0;
    const totalAr = arAgingData.reduce((s, r) => s + r.outstanding, 0);
    const scen = forecast90dData[activeTab] ?? forecast90dData[0];
    const scenChart = forecast90dData.map((s, i) => ({
        name: `M${i + 1}`, pessimistic: s.pessTotal, base: s.baseTotal, optimistic: s.optTotal,
    }));
    const lobTotal = lobDataState.reduce((s, d) => s + d.value, 0);
    const isLoading = anyLoading;
    const isError = anyError;
    return (<div className="cf-page">
      <div className="cf-body">

        
        <div className="cf-toolbar">
          <div className="cf-toolbar__left">
            <div className="cf-toolbar__icon"><RiSparklingFill size={14}/></div>
            <span className="cf-toolbar__title">Commission Forecast</span>
            <span className="cf-toolbar__sep">·</span>
            <span className="cf-toolbar__sub">Agency Intelligence Platform</span>
          </div>
          <div className="cf-toolbar__right">
            <span className={`cf-data-badge cf-data-badge--${status}`}>
              {isLoading ? '● Fetching…' : isError ? '○ Unavailable' : '● Live Data'}
            </span>
            {lastRefreshed && <span className="cf-toolbar__ts">Updated {lastRefreshed}</span>}
            <button className="cf-toolbar__btn" onClick={handleRefresh} disabled={refreshing || isLoading}>
              <FiRefreshCw size={12} className={refreshing || isLoading ? 'cf-spin' : ''}/>
              Refresh
            </button>
          </div>
        </div>

        {isError && (<div className="cf-error-banner">
            <FiAlertTriangle size={17}/>
            <div>
              <strong>Unable to load commission data</strong>
              <p>Could not reach the commission report API. Check your connection or try again.</p>
            </div>
            <button className="cf-toolbar__btn" onClick={handleRefresh}>
              <FiRefreshCw size={12}/> Retry
            </button>
          </div>)}

        
        <div className="cf-section-label">Year-to-Date Snapshot </div>

        {loadingYoy ? (<div className="cf-kpi-grid">
            {Array(5).fill(0).map((_, i) => (<div key={i} className="cf-skel-card cf-skel-card--top">
                <Sk w="58%" h={10} r={4}/>
                <Sk w="46%" h={28} r={5}/>
                <Sk w="72%" h={10} r={4}/>
                <Sk w="38%" h={18} r={99}/>
              </div>))}
          </div>) : errorYoy ? null : !currData ? (<div className="cf-card"><Empty msg="No snapshot data" sub="Year-to-date summary unavailable."/></div>) : (<div className="cf-kpi-grid">
            {[
                { label: 'Commission Earned MTD', val: earned, color: '#34d399', sub: `${currData.PoliciesWritten} active policies`, badge: `${yoyEarnedPct >= 0 ? '▲' : '▼'} ${fmtPct(Math.abs(yoyEarnedPct))} YoY`, bc: yoyEarnedPct >= 0 ? 'green' : 'red' },
                { label: 'Commission Received MTD', val: received, color: '#6366f1', sub: `${fmtPct(collPct)} of earned collected`, badge: outstanding > 0 ? `${fmtK(outstanding)} outstanding` : 'Fully collected', bc: outstanding > 0 ? 'amber' : 'green' },
                { label: '30-Day Forecast', val: total30d, color: '#fbbf24', sub: 'Adjusted for certainty', badge: 'Base case', bc: 'amber' },
                { label: '90-Day Base Case', val: total90dBase, color: '#a78bfa', sub: `${fmtK(total90dPess)}–${fmtK(total90dOpt)} range`, badge: `±${fmtPct(riskPct)} risk range`, bc: 'amber' },
                { label: 'A/R Outstanding', val: totalAr, color: '#ef4444', sub: 'Across all aging buckets', badge: 'Watch — 90+ Days', bc: 'red' },
            ].map(({ label, val, color, sub, badge, bc }, idx) => {
                const tipKey = ['earned', 'received', 'forecast30', 'forecast90', 'ar'][idx];
                return (<div key={label} className="cf-kpi-card" style={{ '--cf-top': color } as any}>
                  <div className="cf-kpi-card__label-row">
                    <span className="cf-kpi-card__label">{label}</span>
                    <InfoTip cfg={KPI_TIPS[tipKey]}/>
                  </div>
                  <div className="cf-kpi-card__value">{fmtK(val)}</div>
                  <div className="cf-kpi-card__sub">{sub}</div>
                  <div className={`cf-badge cf-badge--${bc}`}>{badge}</div>
                </div>);
            })}
          </div>)}

        
        {loadingYoy ? (<div className="cf-metric-grid">
            {Array(3).fill(0).map((_, i) => (<div key={i} className="cf-skel-card cf-skel-card--top">
                <Sk w="55%" h={10} r={4}/>
                <Sk w="36%" h={28} r={5}/>
                <Sk w="80%" h={10} r={4}/>
              </div>))}
          </div>) : errorYoy ? null : (<div className="cf-metric-grid">
            <div className="cf-metric-card cf-metric-card--green">
              <div className="cf-metric-card__label-row">
                <span className="cf-metric-card__label">Policy Retention Rate</span>
                <InfoTip cfg={KPI_TIPS.retention}/>
              </div>
              <div className="cf-metric-card__value cf-metric-card__value--muted">—</div>
              <div className="cf-metric-card__sub">Live data coming soon</div>
            </div>
            <div className="cf-metric-card cf-metric-card--indigo">
              <div className="cf-metric-card__label-row">
                <span className="cf-metric-card__label">Direct Bill vs Agency Bill</span>
                <InfoTip cfg={KPI_TIPS.directBill}/>
              </div>
              <div className="cf-metric-card__value cf-metric-card__value--muted">—</div>
              <div className="cf-metric-card__sub">Live data coming soon</div>
            </div>
            <div className="cf-metric-card cf-metric-card--amber">
              <div className="cf-metric-card__label-row">
                <span className="cf-metric-card__label">At-Risk Policies</span>
                <InfoTip cfg={KPI_TIPS.atRisk}/>
              </div>
              <div className="cf-metric-card__value cf-metric-card__value--red">
                {forecast30dData[2]?.policyCount ?? 0}
              </div>
              <div className="cf-metric-card__sub">Unquoted renewals &amp; pending cancellations</div>
              <div className="cf-badge cf-badge--red">{fmtK(forecast30dData[2]?.commission ?? 0)} commission at risk</div>
            </div>
          </div>)}

        
        <div className="cf-section-label">Year-over-Year Comparison </div>

        {loadingTrailing ? (<div className="cf-yoy-grid">
            <div className="cf-skel-card">
              <Sk w="60%" h={13} r={5}/>
              <Sk w="42%" h={10} r={4}/>
              <Sk w="100%" h={240} r={8}/>
            </div>
            <div className="cf-skel-card">
              <Sk w="60%" h={13} r={5}/>
              <Sk w="42%" h={10} r={4}/>
              {Array(9).fill(0).map((_, i) => (<div key={i} className="cf-skel-row">
                  <Sk w="38%" h={11} r={4}/><Sk w="16%" h={11} r={4}/>
                  <Sk w="16%" h={11} r={4}/><Sk w="18%" h={11} r={4}/>
                </div>))}
            </div>
          </div>) : errorTrailing ? null : trailing12mData.length === 0 ? (<div className="cf-card"><Empty msg="No trend data" sub="Trailing 12-month commission trend unavailable."/></div>) : (<div className="cf-yoy-grid">
            <div className="cf-card">
              <div className="cf-card__header">
                <div className="cf-card__title-row">
                  <div className="cf-card__title">12-month commission trend</div>
                  <InfoTip cfg={CHART_TIPS.trend12m}/>
                </div>
                <div className="cf-card__sub">Monthly commission earned — current year vs prior year</div>
              </div>
              <div className="cf-chart-legend">
                <button className={`cf-legend-toggle${!earnedVis ? ' cf-legend-toggle--off' : ''}`} onClick={() => setEarnedVis(v => !v)}>
                  <span className="cf-legend-toggle__swatch cf-legend-toggle__swatch--purple"/>
                  <span className="cf-legend-toggle__label">Commission Earned</span>
                </button>
                <button className={`cf-legend-toggle${!receivedVis ? ' cf-legend-toggle--off' : ''}`} onClick={() => setReceivedVis(v => !v)}>
                  <span className="cf-legend-toggle__swatch cf-legend-toggle__swatch--green"/>
                  <span className="cf-legend-toggle__label">Commission Received</span>
                </button>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={trailing12mData} margin={{ top: 8, right: 12, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false}/>
                  <XAxis dataKey="MonthLabel" tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.45)' }} axisLine={false} tickLine={false}/>
                  <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.45)' }} axisLine={false} tickLine={false} width={52}/>
                  <Tooltip content={<DarkTooltip />}/>
                  <Line type="monotone" dataKey="CommissionEarned" name="Earned" stroke="#a78bfa" strokeWidth={2} dot={{ r: 3, fill: '#a78bfa', strokeWidth: 0 }} activeDot={{ r: 5 }} hide={!earnedVis}/>
                  <Line type="monotone" dataKey="CommissionReceived" name="Received" stroke="#34d399" strokeWidth={2} strokeDasharray="5 5" dot={{ r: 3, fill: '#34d399', strokeWidth: 0 }} activeDot={{ r: 5 }} hide={!receivedVis}/>
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="cf-card">
              <div className="cf-card__header">
                <div className="cf-card__title">This Month vs Same Month Last Year</div>
                <div className="cf-card__sub">Direct comparison — all commission metrics</div>
              </div>
              {!currData ? (<Empty msg="Comparison unavailable" sub="Current period snapshot not returned."/>) : (<div className="cf-tbl-wrap">
                  <table className="cf-tbl">
                    <thead><tr><th>Metric</th><th>Prior Year</th><th>This Year</th><th>Change</th></tr></thead>
                    <tbody>
                      {[
                    { label: 'Commission Earned', tip: YOY_TIPS.earned, cur: currData.CommissionEarned, prev: smlyData?.CommissionEarned ?? 0 },
                    { label: 'Commission Received', tip: YOY_TIPS.received, cur: currData.CommissionReceived, prev: smlyData?.CommissionReceived ?? 0 },
                    { label: 'Written Premium', tip: YOY_TIPS.wp, cur: currData.WrittenPremium, prev: smlyData?.WrittenPremium ?? 0 },
                    { label: 'New Biz Commission', tip: YOY_TIPS.newBiz, cur: currData.NewBiz_Commission, prev: smlyData?.NewBiz_Commission ?? 0 },
                    { label: 'Renewal Commission', tip: YOY_TIPS.renewal, cur: currData.Renewal_Commission, prev: smlyData?.Renewal_Commission ?? 0 },
                    { label: 'Fee Income', tip: YOY_TIPS.fee, cur: currData.FeeIncome, prev: smlyData?.FeeIncome ?? 0 },
                ].map((row) => (<tr key={row.label}>
                          <td className="cf-tbl__metric cf-tbl__metric--tip"><span>{row.label}</span><InfoTip cfg={row.tip}/></td>
                          <td className="cf-tbl__num">{fmtK(row.prev)}</td>
                          <td className="cf-tbl__num cf-tbl__num--bold">{fmtK(row.cur)}</td>
                          <ChangeCell cur={row.cur} prev={row.prev}/>
                        </tr>))}
                      {[
                    { label: 'Policies Written', tip: YOY_TIPS.policies, cur: currData.PoliciesWritten, prev: smlyData?.PoliciesWritten ?? 0, raw: true },
                    { label: 'Unique Clients', tip: YOY_TIPS.clients, cur: currData.UniqueClients, prev: smlyData?.UniqueClients ?? 0, raw: true },
                    { label: 'Avg Commission / Policy', tip: YOY_TIPS.avgComm, cur: currData.AvgCommPerPolicy, prev: smlyData?.AvgCommPerPolicy ?? 0 },
                ].map((row) => (<tr key={row.label}>
                          <td className="cf-tbl__metric cf-tbl__metric--tip"><span>{row.label}</span><InfoTip cfg={row.tip}/></td>
                          <td className="cf-tbl__num">{row.raw ? fmtNum(row.prev) : fmtK(row.prev)}</td>
                          <td className="cf-tbl__num cf-tbl__num--bold">{row.raw ? fmtNum(row.cur) : fmtK(row.cur)}</td>
                          <ChangeCell cur={row.cur} prev={row.prev} raw={row.raw}/>
                        </tr>))}
                    </tbody>
                  </table>
                </div>)}
            </div>
          </div>)}

        
        {(loadingTrailing || loadingYoy) ? (<div className="cf-charts-row">
            {Array(3).fill(0).map((_, i) => (<div key={i} className="cf-skel-card">
                <Sk w="60%" h={13} r={5}/><Sk w="42%" h={10} r={4}/>
                <Sk w="100%" h={220} r={8}/>
              </div>))}
          </div>) : (errorTrailing && errorYoy) ? null : (<div className="cf-charts-row">

            <div className="cf-card">
              <div className="cf-card__header">
                <div className="cf-card__title-row">
                  <div className="cf-card__title">New business vs renewals</div>
                  <InfoTip cfg={CHART_TIPS.newVsRenew}/>
                </div>
                <div className="cf-card__sub">Commission by business type — trailing 12 months</div>
              </div>
              {trailing12mData.length === 0 ? <Empty msg="No business mix data"/> : (<>
                  <div className="cf-chart-legend">
                    <button className={`cf-legend-toggle${!newBizVis ? ' cf-legend-toggle--off' : ''}`} onClick={() => setNewBizVis(v => !v)}>
                      <span className="cf-legend-toggle__swatch cf-legend-toggle__swatch--indigo"/>
                      <span className="cf-legend-toggle__label">New Business</span>
                    </button>
                    <button className={`cf-legend-toggle${!renewalVis ? ' cf-legend-toggle--off' : ''}`} onClick={() => setRenewalVis(v => !v)}>
                      <span className="cf-legend-toggle__swatch cf-legend-toggle__swatch--green"/>
                      <span className="cf-legend-toggle__label">Renewal</span>
                    </button>
                  </div>
                  <ResponsiveContainer width="100%" height={195}>
                    <BarChart data={trailing12mData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }} barCategoryGap="35%">
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false}/>
                      <XAxis dataKey="MonthLabel" tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.4)' }} axisLine={false} tickLine={false}/>
                      <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.4)' }} axisLine={false} tickLine={false} width={46}/>
                      <Tooltip content={<DarkTooltip />}/>
                      <Bar dataKey="NewBiz_Commission" name="New Business" stackId="s" fill="#6366f1" radius={[0, 0, 0, 0]} hide={!newBizVis}/>
                      <Bar dataKey="Renewal_Commission" name="Renewal" stackId="s" fill="#34d399" radius={[3, 3, 0, 0]} hide={!renewalVis}/>
                    </BarChart>
                  </ResponsiveContainer>
                </>)}
            </div>

            <div className="cf-card">
              <div className="cf-card__header">
                <div className="cf-card__title-row">
                  <div className="cf-card__title">Commission by line of business</div>
                  <InfoTip cfg={CHART_TIPS.lob}/>
                </div>
                <div className="cf-card__sub">PL / CL / Life — current month</div>
              </div>
              {lobDataState.length === 0 || lobTotal === 0 ? (<Empty msg="No LOB data" sub="Line-of-business breakdown unavailable."/>) : (<>
                  <div className="cf-donut-wrap">
                    <ResponsiveContainer width="100%" height={160}>
                      <PieChart>
                        <Pie data={lobDataState} cx="50%" cy="50%" innerRadius={48} outerRadius={74} dataKey="value" paddingAngle={3} startAngle={90} endAngle={-270}>
                          {lobDataState.map((entry, i) => <Cell key={i} fill={entry.color} stroke="transparent"/>)}
                        </Pie>
                        <Tooltip formatter={(v: any) => [fmtK(v), '']} contentStyle={{ background: '#1a1535', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }}/>
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="cf-donut-center">
                      <div className="cf-donut-center__val">{fmtK(lobTotal)}</div>
                      <div className="cf-donut-center__lbl">Total</div>
                    </div>
                  </div>
                  <div className="cf-lob-legend">
                    {lobDataState.map((d) => {
                    const pct = lobTotal > 0 ? ((d.value / lobTotal) * 100).toFixed(1) : '0.0';
                    return (<div key={d.name} className="cf-lob-legend__item">
                          <span className="cf-lob-legend__dot" style={{ background: d.color }}/>
                          <span className="cf-lob-legend__name">{d.name}</span>
                          <span className="cf-lob-legend__val">{fmtK(d.value)}</span>
                          <span className="cf-lob-legend__pct">{pct}%</span>
                        </div>);
                })}
                  </div>
                </>)}
            </div>

            <div className="cf-card">
              <div className="cf-card__header">
                <div className="cf-card__title-row">
                  <div className="cf-card__title">Collected vs outstanding</div>
                  <InfoTip cfg={CHART_TIPS.collectedVsOut}/>
                </div>
                <div className="cf-card__sub">Commission earned vs received by month</div>
              </div>
              {trailing12mData.length === 0 ? <Empty msg="No receivables data"/> : (<>
                  <div className="cf-chart-legend">
                    <button className={`cf-legend-toggle${!collRecVis ? ' cf-legend-toggle--off' : ''}`} onClick={() => setCollRecVis(v => !v)}>
                      <span className="cf-legend-toggle__swatch cf-legend-toggle__swatch--green"/>
                      <span className="cf-legend-toggle__label">Received</span>
                    </button>
                    <button className={`cf-legend-toggle${!collOwedVis ? ' cf-legend-toggle--off' : ''}`} onClick={() => setCollOwedVis(v => !v)}>
                      <span className="cf-legend-toggle__swatch cf-legend-toggle__swatch--amber"/>
                      <span className="cf-legend-toggle__label">Outstanding</span>
                    </button>
                  </div>
                  <ResponsiveContainer width="100%" height={195}>
                    <BarChart data={trailing12mData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false}/>
                      <XAxis dataKey="MonthLabel" tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.4)' }} axisLine={false} tickLine={false}/>
                      <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} tick={{ fontSize: 9, fill: 'rgba(255,255,255,0.4)' }} axisLine={false} tickLine={false} width={46}/>
                      <Tooltip content={<DarkTooltip />}/>
                      <Bar dataKey="CommissionReceived" name="Received" stackId="s" fill="#34d399" radius={[0, 0, 0, 0]} hide={!collRecVis}/>
                      <Bar dataKey="CommissionStillOwed" name="Outstanding" stackId="s" fill="#fbbf24" radius={[3, 3, 0, 0]} hide={!collOwedVis}/>
                    </BarChart>
                  </ResponsiveContainer>
                </>)}
            </div>
          </div>)}

        
        <div className="cf-section-label">30-Day Commission Forecast </div>

        {loading30d ? (<>
            <div className="cf-skel-card cf-skel-card--alert">
              <Sk w={16} h={16} r={4}/><Sk w="70%" h={12} r={4}/>
            </div>
            <div className="cf-bucket-grid">
              {Array(4).fill(0).map((_, i) => (<div key={i} className="cf-skel-card cf-skel-card--top cf-skel-card--bucket">
                  <Sk w="55%" h={10} r={4}/><Sk w="80%" h={10} r={4}/>
                  <Sk w="90%" h={10} r={4}/><Sk w="44%" h={34} r={5}/>
                  <Sk w="50%" h={9} r={4}/>
                </div>))}
            </div>
          </>) : error30d ? null : forecast30dData.length === 0 ? (<div className="cf-card"><Empty msg="No 30-day forecast" sub="30-day commission forecast will appear once policy renewal data is available."/></div>) : (<>
            <div className="cf-alert">
              <FiAlertTriangle size={15} className="cf-alert__icon"/>
              <span>
                <strong>{forecast30dData[2]?.policyCount ?? 0} at-risk policies</strong> represent{' '}
                <strong>{fmtK(forecast30dData[2]?.commission ?? 0)}</strong> in at-risk commission. Review Bucket C for immediate action.
              </span>
            </div>
            <div className="cf-bucket-grid">
              {forecast30dData.map((b, bi) => {
                const bucketTip = [BUCKET_TIPS.a, BUCKET_TIPS.b, BUCKET_TIPS.c, BUCKET_TIPS.d][bi];
                return (<div key={b.label} className="cf-bucket" style={{ '--cf-top': b.color, '--cf-glow': b.borderColor } as any}>
                  <div className="cf-bucket__label-row">
                    <span className="cf-bucket__label" style={{ color: b.color }}>{b.label}</span>
                    {bucketTip && <InfoTip cfg={bucketTip}/>}
                  </div>
                  <div className="cf-bucket__desc">{b.desc}</div>
                  <div className="cf-bucket__gross">
                    {b.policyCount > 0 && <>{fmtNum(b.policyCount)} policies · </>}
                    {fmtK(b.commission)} gross · {(b.certainty * 100).toFixed(0)}% certainty
                  </div>
                  <div className="cf-bucket__value">{fmtK(b.adjusted)}</div>
                  <div className="cf-bucket__value-label">Adjusted forecast</div>
                  <div className="cf-bucket__bar" style={{ background: b.color }}/>
                </div>);
            })}
            </div>
          </>)}

        
        <div className="cf-section-label">90-Day Production Forecast — Three Scenarios</div>

        {loading90d ? (<div className="cf-skel-card cf-skel-card--90d">
            <div className="cf-skel-btn-row">
              <Sk w={110} h={32} r={7}/><Sk w={110} h={32} r={7}/><Sk w={110} h={32} r={7}/>
            </div>
            <Sk w="100%" h={44} r={8}/>
            <div className="cf-skel-grid-3col">
              {Array(3).fill(0).map((_, i) => (<div key={i} className="cf-skel-card cf-skel-card--top">
                  <Sk w="48%" h={10} r={4}/><Sk w="64%" h={10} r={4}/>
                  {Array(3).fill(0).map((__, j) => <Sk key={j} w="100%" h={11} r={4}/>)}
                  <Sk w="40%" h={28} r={5}/>
                </div>))}
            </div>
            <Sk w="100%" h={175} r={8}/>
          </div>) : error90d ? null : forecast90dData.length === 0 ? (<div className="cf-card"><Empty msg="No 90-day forecast" sub="90-day scenario forecast will appear once sufficient trailing data is available."/></div>) : scen ? (<div className="cf-card cf-card--scenarios">
            <div className="cf-scenario-tabs">
              {forecast90dData.map((s, i) => (<button key={i} className={`cf-scenario-tab ${activeTab === i ? 'cf-scenario-tab--active' : ''}`} onClick={() => setActiveTab(i)}>
                  {s.period}
                </button>))}
            </div>
            <div className="cf-vol-bar">
              <span className="cf-vol-bar__text">
                Scenario bands driven by <strong>{scen.volPct}% STDEV</strong> from 12-month trailing volatility
              </span>
              <span className="cf-badge cf-badge--purple">{scen.volLabel}</span>
              <span className="cf-vol-bar__trend">Trend factor: {scen.trendFactor.toFixed(3)}×</span>
            </div>
            <div className="cf-scen-cols">
              {[
                { cls: 'pess', label: 'Pessimistic', stdev: `−${scen.volPct}% STDEV`, ren: scen.pessRenewal, nb: scen.pessNewBiz, tot: scen.pessTotal, col: '#ef4444' },
                { cls: 'base', label: 'Base Case', stdev: `Weighted × ${scen.trendFactor.toFixed(3)}× trend`, ren: scen.baseRenewal, nb: scen.baseNewBiz, tot: scen.baseTotal, col: '#a78bfa' },
                { cls: 'opt', label: 'Optimistic', stdev: `+${scen.volPct}% STDEV`, ren: scen.optRenewal, nb: scen.optNewBiz, tot: scen.optTotal, col: '#34d399' },
            ].map(({ cls, label, stdev, ren, nb, tot, col }) => (<div key={cls} className={`cf-scen-col cf-scen-col--${cls}`}>
                  <div className="cf-scen-col__label">{label}</div>
                  <div className="cf-scen-col__stdev">{stdev}</div>
                  <div className="cf-scen-col__rows">
                    <div className="cf-scen-col__row"><span>Renewal Commission</span><strong>{fmtK(ren)}</strong></div>
                    <div className="cf-scen-col__row"><span>New Business</span><strong>{fmtK(nb)}</strong></div>
                    <div className="cf-scen-col__row"><span>Fee Income</span><strong>{fmtK(scen.feeIncome)}</strong></div>
                  </div>
                  <hr className="cf-scen-col__divider"/>
                  <div className="cf-scen-col__total" style={{ color: col }}>{fmtK(tot)}</div>
                  <div className="cf-scen-col__total-label">Total commission forecast</div>
                </div>))}
            </div>
            <ResponsiveContainer width="100%" height={175}>
              <LineChart data={scenChart} margin={{ top: 8, right: 16, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false}/>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.45)' }} axisLine={false} tickLine={false}/>
                <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} tick={{ fontSize: 10, fill: 'rgba(255,255,255,0.45)' }} axisLine={false} tickLine={false}/>
                <Tooltip content={<DarkTooltip />}/>
                <Line type="monotone" dataKey="optimistic" name="Optimistic" stroke="#34d399" strokeWidth={2} dot={{ r: 4, fill: '#34d399' }} hide={!optVis}/>
                <Line type="monotone" dataKey="base" name="Base Case" stroke="#a78bfa" strokeWidth={2} dot={{ r: 4, fill: '#a78bfa' }} hide={!baseVis}/>
                <Line type="monotone" dataKey="pessimistic" name="Conservative" stroke="#ef4444" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 4, fill: '#ef4444' }} hide={!pessVis}/>
              </LineChart>
            </ResponsiveContainer>
            <div className="cf-chart-legend cf-chart-legend--scenarios">
              <button className={`cf-legend-toggle${!optVis ? ' cf-legend-toggle--off' : ''}`} onClick={() => setOptVis(v => !v)}>
                <span className="cf-legend-toggle__swatch cf-legend-toggle__swatch--green"/>
                <span className="cf-legend-toggle__label">Optimistic</span>
              </button>
              <button className={`cf-legend-toggle${!baseVis ? ' cf-legend-toggle--off' : ''}`} onClick={() => setBaseVis(v => !v)}>
                <span className="cf-legend-toggle__swatch cf-legend-toggle__swatch--purple"/>
                <span className="cf-legend-toggle__label">Base Case</span>
              </button>
              <button className={`cf-legend-toggle${!pessVis ? ' cf-legend-toggle--off' : ''}`} onClick={() => setPessVis(v => !v)}>
                <span className="cf-legend-toggle__swatch cf-legend-toggle__swatch--red"/>
                <span className="cf-legend-toggle__label">Conservative</span>
              </button>
            </div>
          </div>) : null}

        
        <div className="cf-section-label">A/R Aging — Outstanding Receivables by Age</div>

        {loadingAr ? (<div className="cf-skel-card">
            <Sk w="44%" h={13} r={5}/><Sk w="30%" h={10} r={4}/>
            <div className="cf-skel-row cf-skel-row--spaced">
              {['32%', '12%', '16%', '12%', '12%', '10%', '8%'].map((w, i) => <Sk key={i} w={w} h={11} r={4}/>)}
            </div>
            {Array(4).fill(0).map((_, i) => (<div key={i} className="cf-skel-row">
                {['32%', '12%', '16%', '12%', '12%', '10%', '8%'].map((w, j) => <Sk key={j} w={w} h={12} r={4}/>)}
              </div>))}
          </div>) : errorAr ? null : arAgingData.length === 0 ? (<div className="cf-card"><Empty msg="No A/R aging data" sub="Accounts receivable aging buckets will appear once receivables data is available."/></div>) : (<div className="cf-card">
            <div className="cf-card__header">
              <div className="cf-card__title-row">
                <div className="cf-card__title">A/R Aging Buckets</div>
                <InfoTip cfg={CHART_TIPS.arAging}/>
              </div>
              <div className="cf-card__sub">Outstanding receivables by age — all periods</div>
            </div>
            <div className="cf-tbl-wrap">
              <table className="cf-tbl cf-tbl--full">
                <thead>
                  <tr><th>Aging Bucket</th><th>Policies</th><th>Receivables Owed</th><th>Collected</th><th>Outstanding</th><th>Collection %</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {arAgingData.map((row) => (<tr key={row.bucket}>
                      <td className="cf-tbl__metric">{row.bucket}</td>
                      <td className="cf-tbl__num">{fmtNum(row.policies)}</td>
                      <td className="cf-tbl__num">{fmtK(row.owed)}</td>
                      <td className="cf-tbl__num cf-tbl__num--green">{fmtK(row.collected)}</td>
                      <td className="cf-tbl__num cf-tbl__num--red">{fmtK(row.outstanding)}</td>
                      <td className="cf-tbl__num">{fmtPct(row.pct)}</td>
                      <td><span className={`cf-status cf-status--${row.flag.toLowerCase()}`}>{row.flag}</span></td>
                    </tr>))}
                </tbody>
                <tfoot>
                  <tr className="cf-tbl__total-row">
                    <td className="cf-tbl__metric">Total</td>
                    <td className="cf-tbl__num">{fmtNum(arAgingData.reduce((s, r) => s + r.policies, 0))}</td>
                    <td className="cf-tbl__num">{fmtK(arAgingData.reduce((s, r) => s + r.owed, 0))}</td>
                    <td className="cf-tbl__num cf-tbl__num--green">{fmtK(arAgingData.reduce((s, r) => s + r.collected, 0))}</td>
                    <td className="cf-tbl__num cf-tbl__num--red">{fmtK(totalAr)}</td>
                    <td className="cf-tbl__num">{fmtPct(arAgingData.reduce((s, r) => s + r.collected, 0) / Math.max(arAgingData.reduce((s, r) => s + r.owed, 0), 1) * 100)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>)}

        <div className="cf-section-label">Trailing 12-Month Commission Detail </div>

        {loadingTrailing ? (<div className="cf-skel-card">
            <Sk w="50%" h={13} r={5}/><Sk w="36%" h={10} r={4}/>
            <div className="cf-skel-row cf-skel-row--spaced">
              {['14%', '14%', '13%', '13%', '11%', '10%', '10%', '12%'].map((w, i) => <Sk key={i} w={w} h={11} r={4}/>)}
            </div>
            {Array(12).fill(0).map((_, i) => (<div key={i} className="cf-skel-row">
                {['14%', '14%', '13%', '13%', '11%', '10%', '10%', '12%'].map((w, j) => <Sk key={j} w={w} h={12} r={4}/>)}
              </div>))}
          </div>) : errorTrailing ? null : trailing12mData.length === 0 ? (<div className="cf-card"><Empty msg="No trailing data" sub="12-month commission detail will appear once historical data is available."/></div>) : (<div className="cf-card">
            <div className="cf-card__header">
              <div className="cf-card__title-row">
                <div className="cf-card__title">Trailing 12-month commission detail</div>
                <InfoTip cfg={CHART_TIPS.trailing12sec}/>
              </div>
              <div className="cf-card__sub">Month-by-month breakdown — written premium, earned, received, fees</div>
            </div>
            <div className="cf-tbl-wrap">
              <table className="cf-tbl cf-tbl--full">
                <thead>
                  <tr>
                    <th>Month</th>
                    <th><span className="cf-th-wrap">Written Premium <InfoTip cfg={COL_TIPS.writtenPremium}/></span></th>
                    <th><span className="cf-th-wrap">Comm. Earned <InfoTip cfg={COL_TIPS.commEarned}/></span></th>
                    <th><span className="cf-th-wrap">Comm. Received <InfoTip cfg={COL_TIPS.commReceived}/></span></th>
                    <th><span className="cf-th-wrap">% Collected <InfoTip cfg={COL_TIPS.pctCollected}/></span></th>
                    <th><span className="cf-th-wrap">New Biz % <InfoTip cfg={COL_TIPS.newBizPct}/></span></th>
                    <th><span className="cf-th-wrap">Fee Income <InfoTip cfg={COL_TIPS.feeIncome}/></span></th>
                    <th><span className="cf-th-wrap">Total Revenue <InfoTip cfg={COL_TIPS.totalRevenue}/></span></th>
                  </tr>
                </thead>
                <tbody>
                  {trailing12mData.map((row, i) => {
                const isCurr = i === trailing12mData.length - 1;
                const pctCol = row.CommissionEarned > 0 ? (row.CommissionReceived / row.CommissionEarned) * 100 : 0;
                const newBizPct = row.CommissionEarned > 0 ? (row.NewBiz_Commission / row.CommissionEarned) * 100 : 0;
                return (<tr key={row.MonthLabel} className={isCurr ? 'cf-tbl__row--current' : ''}>
                        <td className="cf-tbl__metric">
                          {row.MonthLabel}
                          {isCurr && <span className="cf-curr-badge">◀ current</span>}
                        </td>
                        <td className="cf-tbl__num">{fmtK(row.WrittenPremium)}</td>
                        <td className="cf-tbl__num cf-tbl__num--green">{fmtK(row.CommissionEarned)}</td>
                        <td className="cf-tbl__num">{fmtK(row.CommissionReceived)}</td>
                        <td className={`cf-tbl__num ${pctCol >= 90 ? 'cf-tbl__num--green' : 'cf-tbl__num--red'}`}>{fmtPct(pctCol)}</td>
                        <td className="cf-tbl__num">{fmtPct(newBizPct)}</td>
                        <td className="cf-tbl__num">{fmtK(row.FeeIncome)}</td>
                        <td className="cf-tbl__num">{fmtK(row.TotalRevenue)}</td>
                      </tr>);
            })}
                </tbody>
                <tfoot>
                  <tr className="cf-tbl__total-row">
                    <td className="cf-tbl__metric">12-Month Total</td>
                    <td className="cf-tbl__num">{fmtK(trailing12mData.reduce((s, r) => s + r.WrittenPremium, 0))}</td>
                    <td className="cf-tbl__num cf-tbl__num--green">{fmtK(trailing12mData.reduce((s, r) => s + r.CommissionEarned, 0))}</td>
                    <td className="cf-tbl__num">{fmtK(trailing12mData.reduce((s, r) => s + r.CommissionReceived, 0))}</td>
                    <td className="cf-tbl__num">{fmtPct(trailing12mData.reduce((s, r) => s + r.CommissionReceived, 0) / Math.max(trailing12mData.reduce((s, r) => s + r.CommissionEarned, 0), 1) * 100)}</td>
                    <td className="cf-tbl__num">{fmtPct(trailing12mData.reduce((s, r) => s + r.NewBiz_Commission, 0) / Math.max(trailing12mData.reduce((s, r) => s + r.CommissionEarned, 0), 1) * 100)}</td>
                    <td className="cf-tbl__num">{fmtK(trailing12mData.reduce((s, r) => s + r.FeeIncome, 0))}</td>
                    <td className="cf-tbl__num">{fmtK(trailing12mData.reduce((s, r) => s + r.TotalRevenue, 0))}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>)}

        {!anyLoading && (<div className="cf-footer">
            <span>Source: Momentum AMP Commission Report API · {status === 'live' ? 'Live agency data' : 'Data unavailable'}</span>
            <span>MOMENTUM AMP</span>
          </div>)}

      </div>
    </div>);
};
export default CommissionForecastPage;
